import React, { useState } from 'react';
import './FrameworkExamples.css';

interface FrameworkExample {
  name: string;
  domains: string[];
  primaryDomain: string;
  description: string;
  sampleSteps: string[];
}

const FRAMEWORK_EXAMPLES: FrameworkExample[] = [
  {
    name: 'Bank Reconciliation',
    domains: ['treasury', 'gl'],
    primaryDomain: 'treasury',
    description: 'Match bank statement transactions to general ledger entries, identify timing differences and errors',
    sampleSteps: [
      'Extract bank statement data and GL cash account details',
      'Match cleared transactions using check numbers and amounts',
      'Identify deposits in transit and outstanding checks',
      'Investigate and document unmatched items',
      'Calculate adjusted bank and book balances',
      'Prepare reconciliation report with supporting documentation'
    ]
  },
  {
    name: 'AR Aging Analysis',
    domains: ['ar', 'revenue'],
    primaryDomain: 'ar',
    description: 'Categorize outstanding customer invoices by age to assess collection risk and cash flow',
    sampleSteps: [
      'Extract open AR invoices with dates and amounts',
      'Calculate days outstanding for each invoice',
      'Categorize into aging buckets (0-30, 31-60, 61-90, 90+ days)',
      'Identify credit balances and disputes',
      'Generate summary by customer and total',
      'Flag high-risk accounts for collection action'
    ]
  },
  {
    name: 'Expense Categorization',
    domains: ['operating_expenses', 'gl'],
    primaryDomain: 'operating_expenses',
    description: 'Classify and allocate expenses to appropriate GL accounts and cost centers',
    sampleSteps: [
      'Import expense transactions from various sources',
      'Apply categorization rules based on vendor and description',
      'Allocate to departments/cost centers',
      'Flag unusual or high-value transactions',
      'Generate expense summary by category',
      'Prepare journal entries for GL posting'
    ]
  },
  {
    name: 'Payroll Journal Entry',
    domains: ['payroll', 'operating_expenses', 'gl'],
    primaryDomain: 'payroll',
    description: 'Create journal entries for payroll expenses, taxes, and benefit accruals',
    sampleSteps: [
      'Import payroll register with gross wages and deductions',
      'Calculate employer tax obligations',
      'Compute benefit and PTO accruals',
      'Allocate costs to departments',
      'Generate debit/credit journal entries',
      'Validate total debits equal credits'
    ]
  },
  {
    name: 'Revenue Recognition Waterfall',
    domains: ['revenue', 'ar'],
    primaryDomain: 'revenue',
    description: 'Calculate monthly revenue recognition for subscription or deferred revenue',
    sampleSteps: [
      'Import contract details with start/end dates and amounts',
      'Calculate monthly recognition amounts',
      'Handle mid-month starts and cancellations',
      'Apply revenue recognition rules (ASC 606)',
      'Generate waterfall schedule',
      'Prepare revenue journal entries'
    ]
  }
];

interface FrameworkExamplesProps {
  onSelectExample: (example: FrameworkExample) => void;
}

const FrameworkExamples: React.FC<FrameworkExamplesProps> = ({ onSelectExample }) => {
  const [selectedExample, setSelectedExample] = useState<FrameworkExample | null>(null);
  const [showExamples, setShowExamples] = useState(false);

  const handleSelectExample = (example: FrameworkExample) => {
    setSelectedExample(example);
    onSelectExample(example);
  };

  return (
    <div className="framework-examples">
      <button
        type="button"
        className="toggle-examples-btn"
        onClick={() => setShowExamples(!showExamples)}
      >
        {showExamples ? '▼' : '▶'} View Example Frameworks
      </button>

      {showExamples && (
        <div className="examples-container">
          <div className="examples-grid">
            {FRAMEWORK_EXAMPLES.map((example, idx) => (
              <div
                key={idx}
                className={`example-card ${selectedExample?.name === example.name ? 'selected' : ''}`}
                onClick={() => handleSelectExample(example)}
              >
                <h4>{example.name}</h4>
                <div className="example-domains">
                  {example.domains.join(' + ')}
                </div>
                <p className="example-description">{example.description}</p>
                
                <div className="example-steps">
                  <strong>Sample Steps:</strong>
                  <ul>
                    {example.sampleSteps.slice(0, 3).map((step, stepIdx) => (
                      <li key={stepIdx}>{step}</li>
                    ))}
                    {example.sampleSteps.length > 3 && (
                      <li className="more-steps">...and {example.sampleSteps.length - 3} more steps</li>
                    )}
                  </ul>
                </div>

                <button
                  type="button"
                  className="use-example-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectExample(example);
                  }}
                >
                  Use This Example
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FrameworkExamples;